import React, { useMemo } from "react";
import styled from "styled-components";
import Card from "react-bootstrap/Card";
import ListGroup from "react-bootstrap/ListGroup";
import { defineMessages, FormattedMessage } from "react-intl";
import { Alert } from "react-bootstrap";

export interface ErrorGroup {
  [key: string]: InvalidInventoryStateError[];
}

export interface ErrorContext {
  ticker?: string;
  formula?: string;
}
type ErrorCode =
  | "INVALID_RATE_FORMULA"
  | "INVALID_VALUE"
  | "INVALID_ACTIVE"
  | "INVALID_BOOKING_WINDOW_RANGE"
  | "INVALID_EMPTY_VALUE"
  | "INVALID_ACTION"
  | "INVALID_ROWS"
  | "INVALID_RATE_FORMULA_REFERENCED_ARGUMENT"
  | "INVALID_SHARED_REFERENCE"
  | "INVALID_INVENTORY"
  | "INVALID_DELETE"
  | "INVALID_CREATE";
export interface InvalidInventoryStateError {
  ticker: string;
  relatedTickers: string[];
  valueType: string;
  errorContext: ErrorContext;
  errorCode: ErrorCode;
  errorMessage: string;
}

export interface InventoryResult {
  updatedInventories?: number;
  deletedInventories?: number;
  createdInventories?: number;
  unchangedInventories: number;
  errors: InvalidInventoryStateError[];
  createdVariables: string[];
}

const Container = styled.div``;
const StyledCard = styled(Card)`
  margin-bottom: 16px;
`;

const messages: any = defineMessages({
  createdInventories: {
    id: "multiUpdateInventoryResultRenderer.createdInventories",
    defaultMessage: "Created inventories",
  },
  updatedInventories: {
    id: "multiUpdateInventoryResultRenderer.updatedInventories",
    defaultMessage: "Updated inventories",
  },
  deletedInventories: {
    id: "multiUpdateInventoryResultRenderer.deletedInventories",
    defaultMessage: "Deleted inventories",
  },
  unchangedInventories: {
    id: "multiUpdateInventoryResultRenderer.unchangedInventories",
    defaultMessage: "Unchanged inventories",
  },
  errors: {
    id: "multiUpdateInventoryResultRenderer.errors",
    defaultMessage: "Errors",
  },
  errorList: {
    id: "multiUpdateInventoryResultRenderer.errorList",
    defaultMessage: "Error list",
  },
  createdList: {
    id: "multiUpdateInventoryResultRenderer.createdList",
    defaultMessage: "Created list",
  },
  errorAlert: {
    id: "multiUpdateInventoryResultRenderer.errorAlert",
    defaultMessage: "The following rows/inventories HAVE NOT BEEN PROCESSED",
  },
  valueType: {
    id: "multiUpdateInventoryResultRenderer.valueType",
    defaultMessage: "Value type",
  },
  invalidRateFormula: {
    id: "multiUpdateInventoryResultRenderer.error.invalidRateFormula",
    defaultMessage: "Invalid formula",
  },
  invalidValue: {
    id: "multiUpdateInventoryResultRenderer.error.invalidValue",
    defaultMessage: "Invalid value",
  },
  invalidActive: {
    id: "multiUpdateInventoryResultRenderer.error.invalidActive",
    defaultMessage: "Invalid value for active field",
  },
  invalidBookingWindowRange: {
    id: "multiUpdateInventoryResultRenderer.error.invalidBookingWindowRange",
    defaultMessage: "Invalid booking window range",
  },
  invalidEmptyValue: {
    id: "multiUpdateInventoryResultRenderer.error.invalidEmptyValue",
    defaultMessage: "Invalid empty value",
  },
  invalidAction: {
    id: "multiUpdateInventoryResultRenderer.error.invalidAction",
    defaultMessage: "Invalid action, only possible options are empty or 'Eliminar'",
  },
  invalidRows: {
    id: "multiUpdateInventoryResultRenderer.error.invalidRows",
    defaultMessage:
      "Inventory appears more than once in the ticker column, no action has been taken",
  },
  invalidRateFormulaReferencedArgument: {
    id: "multiUpdateInventoryResultRenderer.error.invalidRateFormulaReferencedArgument",
    defaultMessage:
      "Inventory formula references tickers which do not have OWN rate or do not exist.",
  },
  invalidSharedReference: {
    id: "multiUpdateInventoryResultRenderer.error.invalidSharedReference",
    defaultMessage:
      "Inventory references tickers which are not OWN for this value type do not exist.",
  },
  invalidInventory: {
    id: "multiUpdateInventoryResultRenderer.error.invalidInventory",
    defaultMessage: "Inventory does not exist. You can only modify existing inventories",
  },
  invalidDelete: {
    id: "multiUpdateInventoryResultRenderer.error.invalidDelete",
    defaultMessage: "Inventory cannot be deleted since it's referenced by other inventories",
  },
  invalidCreate: {
    id: "multiUpdateInventoryResultRenderer.error.invalidCreate",
    defaultMessage: "Inventory cannot be created",
  },
  valueTypeNONE: {
    id: "multiUpdateInventoryResultRenderer.valueType.none",
    defaultMessage: "None",
  },
  valueTypeRATE: {
    id: "multiUpdateInventoryResultRenderer.valueType.rate",
    defaultMessage: "Rate",
  },
  valueTypeMAX_NOTICE: {
    id: "multiUpdateInventoryResultRenderer.valueType.maxNotice",
    defaultMessage: "Max notice",
  },
  valueTypeMIN_NOTICE: {
    id: "multiUpdateInventoryResultRenderer.valueType.minNotice",
    defaultMessage: "Min Notice",
  },
  valueTypeMAX_STAY: {
    id: "multiUpdateInventoryResultRenderer.valueType.maxStay",
    defaultMessage: "Max stay",
  },
  valueTypeMIN_STAY: {
    id: "multiUpdateInventoryResultRenderer.valueType.minStay",
    defaultMessage: "Min stay",
  },
  valueTypeLOCK: {
    id: "multiUpdateInventoryResultRenderer.valueType.lock",
    defaultMessage: "Lock",
  },
  valueTypeAVAILABILITY: {
    id: "multiUpdateInventoryResultRenderer.valueType.availability",
    defaultMessage: "Availability",
  },
  valueTypeCHECKIN_DAYS: {
    id: "multiUpdateInventoryResultRenderer.valueType.checkinDays",
    defaultMessage: "Excluded checkin days",
  },
  valueTypeCHECKOUT_DAYS: {
    id: "multiUpdateInventoryResultRenderer.valueType.checkoutDays",
    defaultMessage: "Excluded checkout days",
  },
  valueTypeBOOKING_WINDOW_START: {
    id: "multiUpdateInventoryResultRenderer.valueType.bookingWindowStart",
    defaultMessage: "Booking window start",
  },
  valueTypeBOOKING_WINDOW_END: {
    id: "multiUpdateInventoryResultRenderer.valueType.bookingWindowEnd",
    defaultMessage: "Booking window end",
  },
  valueTypeCODES: {
    id: "multiUpdateInventoryResultRenderer.valueType.codes",
    defaultMessage: "Codes",
  },
  valueTypeACTIVE: {
    id: "multiUpdateInventoryResultRenderer.valueType.active",
    defaultMessage: "Active",
  },
  valueTypeRACK_RATE: {
    id: "multiUpdateInventoryResultRenderer.valueType.rackRate",
    defaultMessage: "Rack rate",
  },
  valueTypeBOOKING_WINDOW: {
    id: "multiUpdateInventoryResultRenderer.valueType.bookingWindow",
    defaultMessage: "Booking window",
  },
  valueTypeCLOSE_TO_DEPARTURE: {
    id: "multiUpdateInventoryResultRenderer.valueType.closeToDeparture",
    defaultMessage: "Close to departure",
  },
  valueTypeCLOSE_TO_ARRIVAL: {
    id: "multiUpdateInventoryResultRenderer.valueType.closeToDeparture",
    defaultMessage: "Close to departure",
  },
  valueTypeACTION: {
    id: "multiUpdateInventoryResultRenderer.valueType.action",
    defaultMessage: "Action",
  },
  error: {
    id: "multiUpdateInventoryResultRenderer.error",
    defaultMessage: "Amount of errors",
  },
  summary: {
    id: "multiUpdateInventoryResultRenderer.summary",
    defaultMessage: "Result summary",
  },
});

function getMessageByErrorCode(error: InvalidInventoryStateError) {
  switch (error.errorCode) {
    case "INVALID_RATE_FORMULA":
      return (
        <span>
          <FormattedMessage {...messages.invalidRateFormula} />: {error.errorContext?.formula}
        </span>
      );
    case "INVALID_VALUE":
      return (
        <span>
          <FormattedMessage {...messages.invalidValue} />
        </span>
      );
    case "INVALID_ACTIVE":
      return (
        <span>
          <FormattedMessage {...messages.invalidActive} />
        </span>
      );
    case "INVALID_BOOKING_WINDOW_RANGE":
      return (
        <span>
          <FormattedMessage {...messages.invalidBookingWindowRange} />
        </span>
      );
    case "INVALID_EMPTY_VALUE":
      return (
        <span>
          <FormattedMessage {...messages.invalidEmptyValue} />
        </span>
      );
    case "INVALID_ACTION":
      return (
        <span>
          <FormattedMessage {...messages.invalidAction} />
        </span>
      );
    case "INVALID_ROWS":
      return (
        <span>
          <FormattedMessage {...messages.invalidRows} />
        </span>
      );
    case "INVALID_RATE_FORMULA_REFERENCED_ARGUMENT":
      return (
        <span>
          <FormattedMessage {...messages.invalidRateFormulaReferencedArgument} />
        </span>
      );
    case "INVALID_SHARED_REFERENCE":
      return (
        <span>
          <FormattedMessage {...messages.invalidSharedReference} />
        </span>
      );
    case "INVALID_INVENTORY":
      return (
        <span>
          <FormattedMessage {...messages.invalidInventory} />
        </span>
      );
    case "INVALID_DELETE":
      return (
        <span>
          <FormattedMessage {...messages.invalidDelete} />: {error.errorContext.formula}
        </span>
      );
    case "INVALID_CREATE":
      return (
        <span>
          <FormattedMessage {...messages.invalidCreate} />: {error.errorMessage}
        </span>
      );
  }
}

function InvalidInventoryStateErrorRenderer({ error }: { error: InvalidInventoryStateError }) {
  const valueTypeMessage = messages[`valueType${error.valueType}`];
  if (!valueTypeMessage) {
    console.debug("Invalid value type", error.valueType);
    return <div></div>;
  }
  return (
    <div>
      <div>
        <FormattedMessage {...messages.valueType} />: <FormattedMessage {...valueTypeMessage} />
      </div>
      <div>{getMessageByErrorCode(error)}</div>
    </div>
  );
}

function InventoryResultRenderer({
  inventoryResult,
  update,
}: {
  inventoryResult: InventoryResult;
  update?: boolean;
}) {
  const sortedErrors: [string, InvalidInventoryStateError[]][] = useMemo(() => {
    const errorGroup = inventoryResult.errors.reduce(
      (acc: ErrorGroup, error: InvalidInventoryStateError) => ({
        ...acc,
        [error.ticker]: [error],
      }),
      {}
    );
    const entries = Object.entries(errorGroup);
    entries.sort((a, b) => {
      if (a[1].length > b[1].length) return -1;
      if (b[1].length > a[1].length) return 1;
      return 0;
    });
    return entries;
  }, [inventoryResult.errors]);
  return (
    <Container>
      <StyledCard>
        <Card.Header as="h5">
          <FormattedMessage {...messages.summary} />
        </Card.Header>
        <ListGroup variant="flush">
          {!update && (
            <ListGroup.Item>
              <FormattedMessage {...messages.createdInventories} />:{" "}
              {inventoryResult.createdInventories}
            </ListGroup.Item>
          )}
          {update && (
            <ListGroup.Item>
              <FormattedMessage {...messages.updatedInventories} />:{" "}
              {inventoryResult.updatedInventories}
            </ListGroup.Item>
          )}
          {update && (
            <ListGroup.Item>
              <FormattedMessage {...messages.deletedInventories} />:{" "}
              {inventoryResult.deletedInventories}
            </ListGroup.Item>
          )}
          {update && (
            <ListGroup.Item>
              <FormattedMessage {...messages.unchangedInventories} />:{" "}
              {inventoryResult.unchangedInventories}
            </ListGroup.Item>
          )}
          <ListGroup.Item>
            <FormattedMessage {...messages.errors} />: {inventoryResult.errors.length}
          </ListGroup.Item>
        </ListGroup>
      </StyledCard>
      {Array.isArray(inventoryResult.createdVariables) &&
        inventoryResult.createdVariables.length > 0 && (
          <StyledCard>
            <Card.Header as="h5">
              <FormattedMessage {...messages.createdList} />
            </Card.Header>
            <ListGroup variant="flush">
              {inventoryResult.createdVariables.map(ticker => (
                <ListGroup.Item>
                  <div>{ticker}</div>
                </ListGroup.Item>
              ))}
            </ListGroup>
          </StyledCard>
        )}

      {sortedErrors && sortedErrors.length > 0 && (
        <StyledCard>
          <Card.Header as="h5">
            <FormattedMessage {...messages.errorList} />
          </Card.Header>
          <Card.Body>
            <Alert variant="danger">
              <strong>
                <FormattedMessage {...messages.errorAlert} />
              </strong>
            </Alert>
          </Card.Body>
          <ListGroup variant="flush">
            {sortedErrors.map(([ticker, tickerErrors]) => (
              <ListGroup.Item>
                <div>
                  Ticker: {ticker} (#
                  <FormattedMessage {...messages.error} />: {tickerErrors.length})
                </div>
                <div>
                  {tickerErrors.map((err: InvalidInventoryStateError) => (
                    <>{err.errorMessage || null}</>
                  ))}
                </div>
                <div>
                  {tickerErrors.map(error => (
                    <InvalidInventoryStateErrorRenderer error={error} />
                  ))}
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </StyledCard>
      )}
    </Container>
  );
}

export default InventoryResultRenderer;
